import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import { 
  AlertTriangle, Bell, CheckCircle2, Clock, RefreshCw, 
  Users, TrendingUp, Activity, Loader2, FileText,
  AlertCircle, ChevronRight, Flame, Heart
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Risk level colors
const riskColors = {
  low: { bg: 'bg-emerald-100', text: 'text-emerald-700', fill: '#10b981' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700', fill: '#f59e0b' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', fill: '#f97316' },
  critical: { bg: 'bg-rose-100', text: 'text-rose-700', fill: '#ef4444' }
};

// Alert severity badge
const SeverityBadge = ({ severity }) => {
  const colors = {
    critical: 'bg-rose-500 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-amber-500 text-white',
    low: 'bg-emerald-500 text-white'
  };
  return (
    <Badge className={colors[severity] || colors.medium}>
      {severity?.toUpperCase()}
    </Badge>
  );
};

// Utilization gauge component
const UtilizationGauge = ({ value, threshold = 85 }) => {
  const color = value >= 95 ? 'bg-rose-500' : value >= 85 ? 'bg-orange-500' : value >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  const status = value >= 95 ? 'Critical' : value >= 85 ? 'High Risk' : value >= 75 ? 'Elevated' : 'Healthy';
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Utilization</span>
        <span className="font-semibold">{value}%</span>
      </div>
      <div className="relative h-3 bg-muted rounded-full overflow-hidden">
        <div 
          className={`absolute inset-y-0 left-0 ${color} transition-all duration-500`}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
        <div 
          className="absolute inset-y-0 w-0.5 bg-black/30"
          style={{ left: `${threshold}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>0%</span>
        <span className="text-orange-500">Threshold: {threshold}%</span>
        <span>100%</span>
      </div>
      <p className={`text-sm font-medium ${value >= 85 ? 'text-orange-600' : 'text-emerald-600'}`}>
        Status: {status}
      </p>
    </div>
  );
};

export default function BurnoutMonitoringDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [acknowledging, setAcknowledging] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [report, setReport] = useState(null);
  const [trends, setTrends] = useState([]);
  const [teamComparison, setTeamComparison] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboard = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/burnout-monitoring/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [token]);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/burnout-monitoring/alerts?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [token]);

  const fetchLatestReport = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/burnout-monitoring/reports/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    }
  }, [token]);

  const fetchTrends = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/burnout-monitoring/trends?periods=12`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrends(data.trends || []);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    }
  }, [token]);

  const fetchTeamComparison = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/burnout-monitoring/team-comparison`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeamComparison(data.teams || []);
      }
    } catch (error) {
      console.error('Error fetching team comparison:', error);
    }
  }, [token]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchAlerts(),
      fetchLatestReport(),
      fetchTrends(),
      fetchTeamComparison()
    ]);
    setLoading(false);
  }, [fetchDashboard, fetchAlerts, fetchLatestReport, fetchTrends, fetchTeamComparison]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/burnout-monitoring/generate-report`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        }
      });
      if (res.ok) {
        const data = await res.json();
        setReport(data);
        toast.success('Burnout report generated', {
          description: `${data.alerts_generated || 0} new alerts created`
        });
        await fetchAlerts();
        await fetchDashboard();
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      toast.error('Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    setAcknowledging(alertId);
    try {
      const res = await fetch(`${API_URL}/api/burnout-monitoring/alerts/acknowledge`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ alert_id: alertId })
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        await fetchAlerts();
        await fetchDashboard();
      }
    } catch (error) {
      toast.error('Error acknowledging alert');
    } finally {
      setAcknowledging(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="burnout-monitoring-page">
      <PageHeader
        icon={Flame}
        title="Burnout Risk Monitoring"
        subtitle="Track staff utilization, identify burnout risks, and receive automated alerts"
        gradient="from-orange-600 via-rose-600 to-red-600"
        action={
          <Button onClick={generateReport} disabled={generating}>
            {generating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Generate Report
          </Button>
        }
      />

      {/* Dashboard Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Current Utilization"
          value={`${dashboard?.current_utilization || 0}%`}
          icon={Activity}
          trend={dashboard?.utilization_status === 'high' ? 'up' : 'stable'}
          status={dashboard?.current_utilization >= 85 ? 'warning' : 'success'}
        />
        <MetricCard
          title="Active Alerts"
          value={dashboard?.active_alerts || 0}
          icon={Bell}
          status={dashboard?.active_alerts > 0 ? 'warning' : 'success'}
        />
        <MetricCard
          title="High Risk Staff"
          value={`${dashboard?.high_risk_staff || 0}/${dashboard?.total_staff || 0}`}
          icon={Users}
          status={dashboard?.high_risk_staff > 0 ? 'warning' : 'success'}
        />
        <MetricCard
          title="Alert Threshold"
          value={`${dashboard?.threshold || 85}%`}
          icon={AlertTriangle}
          status="neutral"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-1">
            Alerts
            {alerts.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {alerts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="teams">Teams</TabsTrigger>
          <TabsTrigger value="report">Report</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Utilization Gauge */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Current Staff Utilization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <UtilizationGauge 
                  value={dashboard?.current_utilization || 0} 
                  threshold={dashboard?.threshold || 85}
                />
                <div className="mt-4 text-sm text-muted-foreground">
                  <p>Latest Period: {dashboard?.latest_period || 'N/A'}</p>
                  <p>Total Staff: {dashboard?.total_staff || 0}</p>
                </div>
              </CardContent>
            </Card>

            {/* Risk Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Risk Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report?.summary?.risk_distribution && (
                  <div className="space-y-3">
                    {Object.entries(report.summary.risk_distribution).map(([level, count]) => (
                      <div key={level} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${riskColors[level]?.bg.replace('100', '500')}`} />
                        <span className="capitalize flex-1">{level}</span>
                        <span className="font-semibold">{count}</span>
                        <span className="text-muted-foreground text-sm">
                          ({report.summary.risk_percentages?.[level]?.toFixed(1)}%)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          {report?.recommendations?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {report.recommendations.map((rec, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                      rec.priority === 'critical' ? 'border-rose-500 bg-rose-50' :
                      rec.priority === 'high' ? 'border-orange-500 bg-orange-50' :
                      rec.priority === 'medium' ? 'border-amber-500 bg-amber-50' :
                      'border-emerald-500 bg-emerald-50'
                    }`}>
                      <div className="flex items-start gap-2">
                        <SeverityBadge severity={rec.priority} />
                        <div>
                          <p className="font-medium">{rec.title}</p>
                          <p className="text-sm text-muted-foreground">{rec.description}</p>
                          {rec.impact && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Impact: {rec.impact}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          {alerts.length === 0 ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>No Active Alerts</AlertTitle>
              <AlertDescription>
                All burnout alerts have been acknowledged or there are no current concerns.
              </AlertDescription>
            </Alert>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <Card key={alert.alert_id} className="border-l-4 border-l-orange-500">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <SeverityBadge severity={alert.severity} />
                            <span className="text-xs text-muted-foreground">
                              {new Date(alert.created_at).toLocaleString()}
                            </span>
                          </div>
                          <h4 className="font-semibold">{alert.title}</h4>
                          <p className="text-sm text-muted-foreground">{alert.message}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => acknowledgeAlert(alert.alert_id)}
                          disabled={acknowledging === alert.alert_id}
                        >
                          {acknowledging === alert.alert_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Utilization Trend</CardTitle>
              <CardDescription>Weekly staff utilization over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={[...trends].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="utilization" 
                    stroke="#f97316" 
                    strokeWidth={2}
                    name="Utilization %"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="high_risk_count" 
                    stroke="#ef4444" 
                    strokeWidth={2}
                    name="High Risk Staff"
                    yAxisId={0}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Team Comparison</CardTitle>
              <CardDescription>Burnout risk by team</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={teamComparison}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="team" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="avg_utilization" name="Utilization %">
                    {teamComparison.map((entry, idx) => (
                      <Cell 
                        key={idx} 
                        fill={riskColors[entry.risk_level]?.fill || '#94a3b8'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {teamComparison.map((team) => (
                  <div key={team.team} className={`p-3 rounded-lg ${riskColors[team.risk_level]?.bg}`}>
                    <p className="font-semibold">{team.team}</p>
                    <p className="text-2xl font-bold">{team.avg_utilization}%</p>
                    <p className="text-xs text-muted-foreground">
                      {team.staff_count} staff • {team.high_risk_count} at risk
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report Tab */}
        <TabsContent value="report" className="space-y-4">
          {report ? (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Monthly Burnout Report</CardTitle>
                      <CardDescription>
                        Generated: {new Date(report.generated_at).toLocaleString()}
                      </CardDescription>
                    </div>
                    <Badge variant={report.summary?.status === 'acceptable' ? 'default' : 'destructive'}>
                      {report.summary?.status?.toUpperCase()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <p className="text-2xl font-bold">{report.summary?.total_clinicians}</p>
                      <p className="text-xs text-muted-foreground">Clinicians</p>
                    </div>
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <p className="text-2xl font-bold">{report.summary?.avg_utilization}%</p>
                      <p className="text-xs text-muted-foreground">Avg Utilization</p>
                    </div>
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <p className="text-2xl font-bold">{report.summary?.periods_analyzed}</p>
                      <p className="text-xs text-muted-foreground">Weeks Analyzed</p>
                    </div>
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <p className="text-2xl font-bold">{report.individual_concerns?.length || 0}</p>
                      <p className="text-xs text-muted-foreground">Staff at Risk</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {report.individual_concerns?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Individual Concerns</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {report.individual_concerns.map((concern, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded border">
                          <div>
                            <p className="font-medium">{concern.clinician_name}</p>
                            <p className="text-xs text-muted-foreground">{concern.team}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{concern.avg_utilization}%</p>
                            <p className="text-xs text-muted-foreground">
                              {concern.high_risk_weeks}/{concern.total_weeks} weeks at risk
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Alert>
              <FileText className="h-4 w-4" />
              <AlertTitle>No Report Available</AlertTitle>
              <AlertDescription>
                Click "Generate Report" to create a new burnout risk assessment.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
